import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone } from "lucide-react";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setMobileOpen(false);
    if (!isLanding) {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navLinks = [
    { label: "How It Works", id: "how-it-works" },
    { label: "Features", id: "features" },
    { label: "Who It's For", id: "use-cases" },
    { label: "Benefits", id: "benefits" },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="section-container flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
          <div className="w-9 h-9 rounded-lg overflow-hidden">
            <img src="https://customer-assets.emergentagent.com/job_bid-quality/artifacts/plxbtywa_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202026-03-12%20%D0%B2%2019.39.09.png" alt="AICallFront" className="w-full h-full object-cover" />
          </div>
          <span className="font-heading text-xl font-bold text-[#0F172A] tracking-tight">
            AICallFront
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors"
              data-testid={`nav-link-${link.id}`}
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Button
            onClick={() => scrollToSection("application-form")}
            className="bg-[#0F172A] text-white h-10 px-6 rounded-lg font-medium hover:bg-[#1E293B] transition-all shadow-sm hover:shadow-md"
            data-testid="nav-cta-button"
          >
            Book a Demo
          </Button>
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-b border-slate-200 shadow-lg animate-fade-in" data-testid="mobile-menu">
          <div className="section-container py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-left text-sm font-medium text-slate-600 hover:text-[#0F172A] py-2 transition-colors"
                data-testid={`mobile-nav-link-${link.id}`}
              >
                {link.label}
              </button>
            ))}
            <Button
              onClick={() => scrollToSection("application-form")}
              className="bg-[#0F172A] text-white h-10 rounded-lg font-medium mt-2"
              data-testid="mobile-nav-cta"
            >
              Book a Demo
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
